"use client";

import clsx from "clsx";
import type { EventLogState } from "@/lib/sse/useSSE";
import { BankBadge } from "@/components/banks/BankBadge";
import { CdmState } from "@/components/cdm/CdmState";
import { LtrToken } from "@/components/cdm/LtrToken";
import { useMoneyFormatter } from "@/lib/i18n/format";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Counterpart view for screens 3.5 / 4.6 / 5.7 (settlement) — buyer's view.
 */

type Variant = "t1" | "t2" | "t3";

const VARIANT_META: Record<Variant, {
  buyer: string;
  seller: string;
  invoiceId: string;
  amountUsd: number;
  bank: "FAB" | "Itau" | null;
  buyerCurrency: string;
  sellerCurrency: string;
  vatJurisdiction: string;
}> = {
  t1: { buyer: "Al Madar Foods LLC", seller: "Aurora Aves Brasil",  invoiceId: "INV-2026-0042", amountUsd: 850_000,   bank: null,    buyerCurrency: "AED", sellerCurrency: "BRL", vatJurisdiction: "UAE FTA" },
  t2: { buyer: "Al Madar Foods LLC", seller: "Aurora Aves Brasil",  invoiceId: "INV-2026-0187", amountUsd: 1_050_000, bank: "FAB",   buyerCurrency: "AED", sellerCurrency: "BRL", vatJurisdiction: "UAE FTA" },
  t3: { buyer: "Pampagro Agronegócios", seller: "Gulf Phosphate Industries UAE", invoiceId: "INV-2026-0342", amountUsd: 3_400_000, bank: "Itau", buyerCurrency: "BRL", sellerCurrency: "AED", vatJurisdiction: "Receita Federal" },
};

export function SettlementImporter({
  ev,
  variant,
  className,
}: {
  ev: EventLogState;
  variant: Variant;
  className?: string;
}) {
  const t     = useTranslations("settlementImporter");
  const meta   = VARIANT_META[variant];
  const fmtUsd = useMoneyFormatter();
  const m      = ev.threeFieldMatched;
  const matched = ev.invoiceState[meta.invoiceId] === "MATCHED";

  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
              {variant === "t3" ? t("kickerT3") : t("kickerDefault")}
            </p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
              {matched ? t("headingMatched") : t("headingPending")}
            </h3>
            <p className="mt-1 text-[12px] text-orbitra-teal-soft">
              {meta.buyer} → {meta.seller} · {fmtUsd(meta.amountUsd)} · invoice <LtrToken>{meta.invoiceId}</LtrToken>
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {meta.bank ? (
              <BankBadge name={meta.bank} fundingPct={variant === "t2" ? 30 : 50} variant="edge" />
            ) : (
              <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed">
                {t("badgeBuyerFunds")}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-3 gap-4">
        <div className="glass col-span-2 flex flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
            {t("railsKicker")}
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Leg
              label={t("outflowLabel", { currency: meta.buyerCurrency })}
              hint={t("outflowHint", { buyer: meta.buyer })}
              dispatched
            />
            <Leg
              label={t("counterpartyLegLabel", { currency: meta.sellerCurrency })}
              hint={t("counterpartyLegHint", { seller: meta.seller })}
              dispatched={!!m}
            />
          </div>

          <div className="mt-5">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("threeFieldTitle")}</p>
            <div className="mt-2 grid grid-cols-3 gap-3 text-[11px]">
              <ThreeField label="VAR"                       ok={m?.var} />
              <ThreeField label="E2E ID"                    ok={m?.e2eId} />
              <ThreeField label={t("fieldInvoiceNumber")}   ok={m?.invoiceNumber} />
            </div>
          </div>

          <div className="mt-auto pt-5">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("invoiceFsmLabel")}</p>
            <div className="mt-2 flex items-center gap-2">
              <CdmState token={matched ? "MATCHED" : "SINGLE_WINDOW_SUBMITTED"} />
              {matched && (
                <span className="rounded-full border border-policy-pass/40 bg-policy-pass/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-policy-pass">
                  {t("clearedBadge")}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="glass p-4 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{meta.vatJurisdiction} · audit pack</p>
            <p className="mt-2 text-orbitra-paper">
              {t("vatAuditBody")}
            </p>
            <p className="mt-2 text-[10px] text-orbitra-muted">
              {t("vatAuditNote")}
            </p>
          </div>

          <div className="glass p-4 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("whatStaysHidden")}</p>
            <ul className="mt-2 space-y-1 text-orbitra-teal-soft">
              <li>{t("hiddenItem1")}</li>
              <li>{t("hiddenItem2")}</li>
              <li>{t("hiddenItem3")}</li>
            </ul>
            <p className="mt-2 text-[10px] text-orbitra-muted">
              {t("footerText")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Leg({ label, hint, dispatched }: { label: string; hint: string; dispatched: boolean }) {
  const t = useTranslations("settlementImporter");
  return (
    <div className={clsx(
      "rounded-md border px-3 py-3 text-[11px]",
      dispatched
        ? "border-state-financed/50 bg-state-financed/8 text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]"
        : "border-orbitra-muted/30 bg-orbitra-ink/30 text-orbitra-muted",
    )}>
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-1 font-display text-base font-semibold text-orbitra-paper">
        {dispatched ? t("legDispatched") : t("legPending")}
      </p>
      <p className="mt-1 text-[10px]">{hint}</p>
    </div>
  );
}

function ThreeField({ label, ok }: { label: string; ok?: boolean }) {
  const t = useTranslations("settlementImporter");
  return (
    <div className={clsx(
      "flex items-center justify-between rounded-md border px-3 py-2",
      ok ? "border-policy-pass/40 bg-policy-pass/10 text-policy-pass" : "border-orbitra-muted/30 text-orbitra-muted",
    )}>
      <span className="font-mono text-[10px] uppercase tracking-wider">{label}</span>
      <span className="text-[10px]">{ok ? t("threeFieldMatched") : "·"}</span>
    </div>
  );
}
